import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { Pagination as BSPagniation, PaginationItem, PaginationLink } from 'reactstrap'

interface PaginationProps {
  page: number
  setPage: (page: number) => void
  pageCount: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

interface PageItem {
  page: number
  active: boolean
}

export function buildPaginationPages (page: number, pageCount: number): PageItem[] {
  const MAX = 6
  const HALF = Math.floor(MAX / 2) - 1
  if (pageCount <= 0) {
    return []
  }

  // Clamp current page into [1, pageCount]
  const current = Math.min(Math.max(1, page), pageCount)

  let start: number
  let end: number

  if (pageCount <= MAX) {
    start = 1
    end = pageCount
  } else if (current <= 5) {
    start = 1
    end = MAX
  } else if (current >= pageCount - 4) {
    start = Math.max(1, pageCount - (MAX - 1))
    end = pageCount
  } else {
    // Center: 4 to the left, 5 to the right
    start = current - HALF
    end = current + (MAX - HALF - 1)
  }

  const items: PageItem[] = []
  for (let p = start; p <= end && p <= pageCount; p++) {
    items.push({ page: p, active: p === current })
  }
  return items
}

const Pagination: React.FC<PaginationProps> = (props) => {
  const { page, setPage, pageCount, hasNextPage, hasPreviousPage } = props
  if (pageCount <= 1) {
    return null
  }
  if (page > pageCount || page < 1) {
    throw new Error(`Invalid page number: ${page}. Must be between 1 and ${pageCount}.`)
  }

  const handlePageChange = (newPage: number): void => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage)
    }
  }

  const handleNextPage = (): void => {
    if (hasNextPage) {
      setPage(page + 1)
    }
  }

  const handlePreviousPage = (): void => {
    if (hasPreviousPage) {
      setPage(page - 1)
    }
  }

  const handleFirstPage = (): void => {
    if (page > 1) {
      setPage(1)
    }
  }

  const handleLastPage = (): void => {
    if (page < props.pageCount) {
      setPage(props.pageCount)
    }
  }

  const pages = buildPaginationPages(page, pageCount)

  return (
    <>
      <div className='text-center mb-3'>
        Page {page} of {pageCount}
      </div>
      <BSPagniation size='sm' className='justify-content-center'>
        <PaginationItem disabled={page <= 1} onClick={handleFirstPage}>
          <PaginationLink href='#'>
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem disabled={!props.hasPreviousPage} onClick={handlePreviousPage}>
          <PaginationLink href='#'>
            <FontAwesomeIcon icon={faAngleLeft} />
          </PaginationLink>
        </PaginationItem>
        {pages.map(({ page, active }, index) => {
          return (
            <PaginationItem
              key={page}
              active={active}
              onClick={() => handlePageChange(page)}
            >
              <PaginationLink href='#'>
                {page}
              </PaginationLink>
            </PaginationItem>
          )
        })}
        <PaginationItem disabled={!props.hasNextPage} onClick={handleNextPage}>
          <PaginationLink href='#'>
            <FontAwesomeIcon icon={faAngleRight} />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem disabled={page >= pageCount} onClick={handleLastPage}>
          <PaginationLink href='#'>
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </PaginationLink>
        </PaginationItem>
      </BSPagniation>
    </>
  )
}

export default Pagination
